// Philippine VAT rate per NIRC §106 (12% standard rate). EOPT Act (RA 11976) did not
// change the rate — only the invoicing rules. Keep this constant in one place.
export const PH_VAT_RATE = 0.12;

import type {
  InvoiceLineItem,
  InvoiceTotals,
  RegistrationMode,
  SeniorCitizenDiscount,
} from './types';

/** Round to centavos (2dp), half-away-from-zero, with an epsilon bias to neutralise
 *  IEEE-754 representation drift (e.g. 1.005 → 1.005000…2 → rounds correctly). */
export function roundCentavos(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

export function lineGross(line: InvoiceLineItem): number {
  return roundCentavos(line.unitPrice * line.quantity);
}

export function computeTotals(
  lines: readonly InvoiceLineItem[],
  discount?: { amount: number },
): InvoiceTotals {
  let vatableNet = 0;
  let exempt = 0;
  let zeroRated = 0;

  for (const line of lines) {
    const gross = lineGross(line);
    if (line.vatClass === 'vatable') vatableNet += gross;
    else if (line.vatClass === 'exempt') exempt += gross;
    else zeroRated += gross;
  }

  const discountAmt = discount?.amount ?? 0;
  // Discount is applied to the vatable subtotal first, per BIR convention for senior/PWD.
  const vatableAfterDiscount = Math.max(0, vatableNet - discountAmt);
  const vatAmount = roundCentavos(vatableAfterDiscount * PH_VAT_RATE);
  const vatableSalesGross = roundCentavos(vatableAfterDiscount + vatAmount);
  const totalAmountDue = roundCentavos(vatableSalesGross + exempt + zeroRated);

  return {
    vatableSalesNetOfVat: roundCentavos(vatableAfterDiscount),
    vatAmount,
    vatableSalesGross,
    exemptSales: roundCentavos(exempt),
    zeroRatedSales: roundCentavos(zeroRated),
    discount: roundCentavos(discountAmt),
    totalAmountDue,
  };
}

/**
 * Compute totals when prices are quoted VAT-inclusive (gross) and 12% VAT must
 * be *extracted* from the line totals. Used for the "Sales Invoice" mode where
 * the seller is BIR-registered and the price-tag already includes VAT.
 *
 *   net  = gross / 1.12
 *   vat  = gross - net     (≡ gross × 12 / 112)
 */
export function computeTotalsVatInclusive(
  lines: readonly InvoiceLineItem[],
  discount?: { amount: number },
): InvoiceTotals {
  let vatableGross = 0;
  let exempt = 0;
  let zeroRated = 0;

  for (const line of lines) {
    const gross = lineGross(line);
    if (line.vatClass === 'vatable') vatableGross += gross;
    else if (line.vatClass === 'exempt') exempt += gross;
    else zeroRated += gross;
  }

  const discountAmt = discount?.amount ?? 0;
  const vatableGrossAfterDiscount = Math.max(0, vatableGross - discountAmt);
  // Extract: net = gross / 1.12, vat = gross - net.
  const vatableNet = roundCentavos(vatableGrossAfterDiscount / (1 + PH_VAT_RATE));
  const vatAmount = roundCentavos(vatableGrossAfterDiscount - vatableNet);
  const totalAmountDue = roundCentavos(vatableGrossAfterDiscount + exempt + zeroRated);

  return {
    vatableSalesNetOfVat: vatableNet,
    vatAmount,
    vatableSalesGross: roundCentavos(vatableGrossAfterDiscount),
    exemptSales: roundCentavos(exempt),
    zeroRatedSales: roundCentavos(zeroRated),
    discount: roundCentavos(discountAmt),
    totalAmountDue,
  };
}

/**
 * Compute totals for a non-VAT (unregistered / freemium) taxpayer. The seller
 * may not legally charge or break out VAT, so all VAT columns are zero and the
 * total is the simple sum of line totals.
 */
export function computeTotalsNonVat(
  lines: readonly InvoiceLineItem[],
  discount?: { amount: number },
): InvoiceTotals {
  let subtotal = 0;
  for (const line of lines) subtotal += lineGross(line);
  const discountAmt = discount?.amount ?? 0;
  const total = Math.max(0, subtotal - discountAmt);
  return {
    vatableSalesNetOfVat: 0,
    vatAmount: 0,
    vatableSalesGross: 0,
    exemptSales: 0,
    zeroRatedSales: 0,
    discount: roundCentavos(discountAmt),
    totalAmountDue: roundCentavos(total),
  };
}

/**
 * Apply RA 9994 / RA 10754 Senior Citizen / PWD statutory math to a VAT-inclusive
 * gross vatable subtotal. Returns the share-of-VAT to remove, the net senior
 * subtotal (which becomes VAT-exempt sales on the invoice), the 20% statutory
 * discount, and the leftover vatable gross still subject to 12% VAT.
 *
 *   SeniorShare    = gross / numberOfPax
 *   NetSenior      = SeniorShare / 1.12
 *   SeniorVAT      = SeniorShare - NetSenior
 *   SeniorDiscount = NetSenior × 0.20
 *
 * Final payable = gross - SeniorVAT - SeniorDiscount.
 */
function seniorSplit(
  vatableGross: number,
  numberOfPax: number,
): {
  seniorShareGross: number;
  netSenior: number;
  seniorVat: number;
  seniorDiscount: number;
  remainingVatableGross: number;
} {
  const pax = Math.max(1, Math.floor(numberOfPax));
  const seniorShareGross = vatableGross / pax;
  const netSenior = seniorShareGross / (1 + PH_VAT_RATE);
  const seniorVat = seniorShareGross - netSenior;
  const seniorDiscount = netSenior * 0.2;
  const remainingVatableGross = vatableGross - seniorShareGross;
  return {
    seniorShareGross: roundCentavos(seniorShareGross),
    netSenior: roundCentavos(netSenior),
    seniorVat: roundCentavos(seniorVat),
    seniorDiscount: roundCentavos(seniorDiscount),
    remainingVatableGross: roundCentavos(remainingVatableGross),
  };
}

function computeTotalsRegisteredWithSenior(
  lines: readonly InvoiceLineItem[],
  senior: SeniorCitizenDiscount,
  discount?: { amount: number },
): InvoiceTotals {
  let vatableGross = 0;
  let exempt = 0;
  let zeroRated = 0;
  for (const line of lines) {
    const gross = lineGross(line);
    if (line.vatClass === 'vatable') vatableGross += gross;
    else if (line.vatClass === 'exempt') exempt += gross;
    else zeroRated += gross;
  }

  const flatDiscount = discount?.amount ?? 0;
  const vatableGrossAfterFlat = Math.max(0, vatableGross - flatDiscount);

  const split = seniorSplit(vatableGrossAfterFlat, senior.numberOfPax);

  const remainingNet = roundCentavos(split.remainingVatableGross / (1 + PH_VAT_RATE));
  const remainingVat = roundCentavos(split.remainingVatableGross - remainingNet);

  const totalAmountDue = roundCentavos(
    split.remainingVatableGross + split.netSenior - split.seniorDiscount + exempt + zeroRated,
  );

  return {
    vatableSalesNetOfVat: remainingNet,
    vatAmount: remainingVat,
    vatableSalesGross: split.remainingVatableGross,
    exemptSales: roundCentavos(exempt),
    zeroRatedSales: roundCentavos(zeroRated),
    discount: roundCentavos(flatDiscount + split.seniorDiscount),
    totalAmountDue,
    seniorVatExempt: split.netSenior,
    seniorDiscountAmount: split.seniorDiscount,
  };
}

function computeTotalsNonVatWithSenior(
  lines: readonly InvoiceLineItem[],
  senior: SeniorCitizenDiscount,
  discount?: { amount: number },
): InvoiceTotals {
  let subtotal = 0;
  for (const line of lines) subtotal += lineGross(line);
  const flatDiscount = discount?.amount ?? 0;
  const baseAfterFlat = Math.max(0, subtotal - flatDiscount);
  const pax = Math.max(1, Math.floor(senior.numberOfPax));
  const seniorShare = baseAfterFlat / pax;
  const seniorDiscount = roundCentavos(seniorShare * 0.2);
  const total = roundCentavos(baseAfterFlat - seniorDiscount);
  return {
    vatableSalesNetOfVat: 0,
    vatAmount: 0,
    vatableSalesGross: 0,
    exemptSales: 0,
    zeroRatedSales: 0,
    discount: roundCentavos(flatDiscount + seniorDiscount),
    totalAmountDue: total,
    seniorVatExempt: roundCentavos(seniorShare),
    seniorDiscountAmount: seniorDiscount,
  };
}

/** Dispatch helper — picks the right totals routine for the registration mode. */
export function computeTotalsForMode(
  lines: readonly InvoiceLineItem[],
  mode: RegistrationMode,
  discount?: { amount: number },
  senior?: SeniorCitizenDiscount,
): InvoiceTotals {
  if (senior) {
    return mode === 'unregistered'
      ? computeTotalsNonVatWithSenior(lines, senior, discount)
      : computeTotalsRegisteredWithSenior(lines, senior, discount);
  }
  return mode === 'unregistered'
    ? computeTotalsNonVat(lines, discount)
    : computeTotalsVatInclusive(lines, discount);
}

const PHP_FORMATTER = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
});

export function formatPhp(amount: number): string {
  return PHP_FORMATTER.format(amount);
}
